import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorAlert } from '../../components/common/ErrorAlert';
import { LoadingState } from '../../components/common/LoadingState';
import { PasswordField } from '../../components/common/PasswordField';
import { ShellCard } from '../../components/layout/ShellCard';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { useFeedback } from '../../components/common/FeedbackProvider';
import { useAdminUsers } from '../../hooks/useAdminUsers';
import { useAuth } from '../../hooks/useAuth';
import { adminService } from '../../services/adminService';
import { roleLabel } from '../../utils/labels';
import { tokens } from '../../theme/tokens';

function randomPassword() {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 10; i += 1) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

const ROLES = ['admin', 'maker', 'checker', 'reader'];

const emptyCreate = {
  name: '',
  email: '',
  role: 'reader',
  password: '',
  phone: '',
  address: '',
};

export function UsersTab() {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const { user: currentUser } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const {
    items,
    loading,
    error,
    q,
    setQ,
    role,
    setRole,
    create,
    update,
    resetPassword,
    busy,
    reload,
    meta,
    loadMore,
  } = useAdminUsers();

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [engagementUser, setEngagementUser] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [editForm, setEditForm] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState(null);

  async function onCreate(e) {
    e.preventDefault();
    if ((createForm.password || '').length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }
    try {
      await create({
        name: createForm.name.trim(),
        email: createForm.email.trim(),
        role: createForm.role,
        password: createForm.password,
        phone: createForm.phone.trim() || undefined,
        address: createForm.address.trim() || undefined,
      });
      showSuccess('User created');
      setCreateOpen(false);
      setCreateForm(emptyCreate);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Create failed');
    }
  }

  async function saveEdit() {
    if (!editUser) return;
    await update(editUser.id, {
      name: editForm.name?.trim(),
      role: editForm.role,
      phone: editForm.phone?.trim() || null,
      address: editForm.address?.trim() || null,
      active: editForm.active,
    });
    showSuccess(
      editForm.active === false ? 'Account deactivated' : 'User updated',
    );
    setEditUser(null);
  }

  async function onEdit(e) {
    e.preventDefault();
    if (!editUser) return;
    const deactivating =
      editUser.active !== false && editForm.active === false;
    if (deactivating) {
      setConfirm({
        title: `Deactivate ${editUser.name}?`,
        message: 'They will not be able to sign in until you turn the account back on.',
        confirmLabel: 'Deactivate',
        danger: true,
        run: () => saveEdit(),
      });
      return;
    }
    try {
      await saveEdit();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Update failed');
    }
  }

  async function onResetPassword(e) {
    e.preventDefault();
    if (!resetUser) return;
    if (newPassword.length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }
    try {
      await resetPassword(resetUser.id, newPassword);
      showSuccess('Password reset. Share it with them — they stay signed in until they sign out.');
      setResetUser(null);
      setNewPassword('');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Reset failed');
    }
  }

  function openEdit(u) {
    setEditUser(u);
    setEditForm({
      name: u.name,
      role: u.role,
      phone: u.phone ?? '',
      address: u.address ?? '',
      active: u.active !== false,
    });
  }

  async function openEngagement(u) {
    setEngagementUser(u);
    setEngagement(null);
    setEngagementLoading(true);
    try {
      const data = await adminService.userEngagement(u.id);
      setEngagement(data);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load engagement');
      setEngagementUser(null);
    } finally {
      setEngagementLoading(false);
    }
  }

  return (
    <Stack spacing={2}>
      <ErrorAlert message={error} onClose={() => reload()} />

      <ShellCard
        sx={{ overflow: 'hidden' }}
        contentSx={{ p: 0, '&:last-child': { pb: 0 } }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.5, sm: 1.75 },
            borderBottom: `1px solid ${tokens.border}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            flexWrap="wrap"
            useFlexGap
            sx={{ flex: 1, alignItems: 'center' }}
          >
            <TextField
              size="small"
              label="Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, flex: { xs: '1 1 100%', sm: '1 1 200px' } }}
            />
            <FormControl
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 140 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
            >
              <InputLabel id="users-role-filter-label" shrink>
                Role
              </InputLabel>
              <Select
                labelId="users-role-filter-label"
                label="Role"
                value={role}
                notched
                onChange={(e) => setRole(e.target.value)}
              >
                <MenuItem value="all">All roles</MenuItem>
                {ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {roleLabel(r)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setCreateOpen(true)}
            sx={{ color: '#fff', alignSelf: { xs: 'stretch', sm: 'center' } }}
          >
            Create user
          </Button>
        </Box>

        {loading && (
          <Box sx={{ p: 2 }}>
            <LoadingState label="Loading users…" />
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Box sx={{ p: 2 }}>
            <EmptyState
              title="No users found"
              description="Try a different search or create a user."
            />
          </Box>
        )}

        {!loading &&
          items.map((u, index) => (
            <Box key={u.id}>
              {index > 0 ? <Divider /> : null}
              <Box sx={{ px: { xs: 1.75, sm: 2.25 }, py: { xs: 1.75, sm: 2 } }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700}>{u.name}</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {u.email}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1 }}
                    >
                      <Chip
                        size="small"
                        label={roleLabel(u.role)}
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={u.active !== false ? 'Active' : 'Inactive'}
                        color={u.active !== false ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                      '& .MuiButton-root': {
                        flex: { xs: '1 1 auto', sm: '0 0 auto' },
                      },
                    }}
                  >
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => void openEngagement(u)}
                    >
                      Activity
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openEdit(u)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setResetUser(u);
                        setNewPassword('');
                      }}
                    >
                      Reset password
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          ))}

        {!loading && meta?.total > items.length && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Showing {items.length} of {meta.total}
            </Typography>
            <Button variant="outlined" onClick={loadMore} disabled={busy}>
              Load more
            </Button>
          </Box>
        )}
        {!loading && meta?.total > 0 && meta.total <= items.length && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {meta.total === 1 ? '1 user' : `${meta.total} users`}
            </Typography>
          </Box>
        )}
      </ShellCard>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreenDialog}
      >
        <Box component="form" onSubmit={onCreate}>
          <DialogTitle>Create user</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Name"
                required
                fullWidth
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              />
              <TextField
                label="Email"
                type="email"
                required
                fullWidth
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
              />
              <FormControl fullWidth required>
                <InputLabel id="create-user-role-label" shrink>
                  Role
                </InputLabel>
                <Select
                  labelId="create-user-role-label"
                  label="Role"
                  notched
                  value={createForm.role}
                  onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}
                >
                  {ROLES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {roleLabel(r)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <PasswordField
                label="Temporary password"
                required
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                autoComplete="new-password"
                helperText="At least 6 characters"
              />
              <Button
                type="button"
                size="small"
                onClick={() => {
                  const next = randomPassword();
                  setCreateForm((f) => ({ ...f, password: next }));
                  void navigator.clipboard?.writeText(next);
                  showSuccess('Password generated and copied');
                }}
              >
                Generate password
              </Button>
              <TextField
                label="Phone"
                fullWidth
                value={createForm.phone}
                onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
              />
              <TextField
                label="Address"
                fullWidth
                multiline
                rows={2}
                value={createForm.address}
                onChange={(e) => setCreateForm((f) => ({ ...f, address: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={busy}>
              Create
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(editUser)}
        onClose={() => setEditUser(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreenDialog}
      >
        <Box component="form" onSubmit={onEdit}>
          <DialogTitle>Edit user</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Name"
                required
                fullWidth
                value={editForm.name ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
              <FormControl fullWidth>
                <InputLabel id="edit-user-role-label" shrink>
                  Role
                </InputLabel>
                <Select
                  labelId="edit-user-role-label"
                  label="Role"
                  notched
                  value={editForm.role ?? 'reader'}
                  onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                  disabled={editUser?.id === currentUser?.id}
                >
                  {ROLES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {roleLabel(r)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {editUser?.id === currentUser?.id ? (
                <Typography variant="caption" color="text.secondary">
                  You cannot change your own role or turn off your own account.
                </Typography>
              ) : null}
              <TextField
                label="Phone"
                fullWidth
                value={editForm.phone ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Address"
                fullWidth
                multiline
                rows={2}
                value={editForm.address ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth>
                <InputLabel id="edit-user-status-label" shrink>
                  Status
                </InputLabel>
                <Select
                  labelId="edit-user-status-label"
                  label="Status"
                  notched
                  value={editForm.active === false ? 'inactive' : 'active'}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      active: e.target.value === 'active',
                    }))
                  }
                  disabled={editUser?.id === currentUser?.id}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUser(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={busy}>
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(resetUser)}
        onClose={() => setResetUser(null)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreenDialog}
      >
        <Box component="form" onSubmit={onResetPassword}>
          <DialogTitle>Reset password</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set a new password for {resetUser?.name}
            </Typography>
            <PasswordField
              label="New password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              helperText="At least 6 characters"
            />
            <Button
              type="button"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => {
                const next = randomPassword();
                setNewPassword(next);
                void navigator.clipboard?.writeText(next);
                showSuccess('Password generated and copied');
              }}
            >
              Generate password
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetUser(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={busy}>
              Reset
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog
        open={Boolean(engagementUser)}
        onClose={() => setEngagementUser(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>Activity — {engagementUser?.name}</DialogTitle>
        <DialogContent>
          {engagementLoading && <LoadingState label="Loading activity…" />}
          {engagement && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Alerts
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    ['Opens', engagement.totals?.opens ?? 0],
                    ['Clicks', engagement.totals?.clicks ?? 0],
                    ['Reactions', engagement.totals?.reactions ?? 0],
                    ['Shares', engagement.totals?.shares ?? 0],
                  ].map(([label, value]) => (
                    <Grid item xs={6} key={`alert-${label}`}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="h6">{value}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Articles
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    ['Views', engagement.articles?.totals?.views ?? 0],
                    ['Shares', engagement.articles?.totals?.shares ?? 0],
                    ['Reacts', engagement.articles?.totals?.reacts ?? 0],
                    ['Comments', engagement.articles?.totals?.comments ?? 0],
                  ].map(([label, value]) => (
                    <Grid item xs={6} key={`article-${label}`}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="h6">{value}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEngagementUser(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={Boolean(confirm?.danger)}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const run = confirm?.run;
          setConfirm(null);
          if (!run) return;
          void Promise.resolve(run()).catch((err) =>
            showError(err instanceof Error ? err.message : 'Could not finish'),
          );
        }}
      />
    </Stack>
  );
}
